import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function currentPeriod() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function StatusBadge({ status, isZh }) {
  const styles = {
    released: { backgroundColor: 'rgba(201,169,97,0.2)', color: '#c9a961', label: isZh ? '已发放' : 'Released' },
    pending: { backgroundColor: 'rgba(201,169,97,0.05)', color: '#b8a68f', label: isZh ? '待发放' : 'Pending' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className="px-2 py-1 rounded text-body-xs font-medium" style={{ backgroundColor: s.backgroundColor, color: s.color }}>
      {s.label}
    </span>
  );
}

function NumberEditor({ label, value, onSave, saving, suffix, min = 0, max = 100, allowClear = false, isZh }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const startEdit = () => {
    setEditValue(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  };

  const save = async () => {
    if (allowClear && editValue.trim() === '') {
      await onSave(null);
      setEditing(false);
      return;
    }
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < min || (max !== null && parsed > max)) {
      alert(isZh
        ? `请输入一个${max !== null ? `${min} 到 ${max} 之间的` : `不小于 ${min} 的`}数字${allowClear ? '，或留空以清除' : ''}`
        : `Please enter a number${max !== null ? ` between ${min} and ${max}` : ` >= ${min}`}${allowClear ? ', or leave blank to clear' : ''}`);
      return;
    }
    await onSave(parsed);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
      <span className="text-body-sm" style={{ color: '#b8a68f' }}>{label}</span>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            min={min}
            max={max ?? undefined}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            disabled={saving}
            placeholder={allowClear ? (isZh ? '留空 = 无' : 'blank = none') : ''}
            className="w-24 px-2 py-1 text-sm rounded border"
            style={{ borderColor: '#3d5a4a' }}
          />
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {saving ? (isZh ? '保存中...' : 'Saving...') : (isZh ? '保存' : 'Save')}
          </button>
          <button
            onClick={() => setEditing(false)}
            disabled={saving}
            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
            style={{ backgroundColor: '#3d5a4a', color: '#b8a68f' }}
          >
            {isZh ? '取消' : 'Cancel'}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="font-medium" style={{ color: '#c9a961' }}>{value === null || value === undefined ? '—' : value}{value !== null && value !== undefined ? (suffix !== undefined ? suffix : '%') : ''}</span>
          <button
            onClick={startEdit}
            className="px-3 py-1 text-xs rounded font-medium"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {isZh ? '编辑' : 'Edit'}
          </button>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
      style={
        active
          ? { color: '#0d2818', backgroundColor: '#c9a961' }
          : { color: '#c9a961', backgroundColor: 'transparent' }
      }
    >
      {children}
    </button>
  );
}

export default function TeamBonus() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const [activeTab, setActiveTab] = useState('bonus');
  const [period, setPeriod] = useState(currentPeriod());
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const [run, setRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState({});
  const [statusTab, setStatusTab] = useState('pending');

  const [payoutRates, setPayoutRates] = useState([]);
  const [starRates, setStarRates] = useState([]);
  const [savingRateKey, setSavingRateKey] = useState(null);

  const [modalRecord, setModalRecord] = useState(null);
  const [modalMembers, setModalMembers] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/team-bonus-v2/list?year=${period.year}&month=${period.month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Team Bonus');
      }
      setRun(data.run);
      setRecords(data.records || []);
      setSelectedIds({});
    } catch (err) {
      setError(err.message || tt('Failed to load Team Bonus', '加载团队奖金失败'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus-v2/rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load rates');
      }
      setPayoutRates(data.payout_rates || []);
      setStarRates(data.star_rates || []);
    } catch (err) {
      setError(err.message || tt('Failed to load rates', '加载比例失败'));
    }
  };

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, period.year, period.month]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleCalculate = async () => {
    setCalculating(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus-v2/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ period_year: period.year, period_month: period.month }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to calculate', '计算失败'));
      }
      setStatusMessage(tt(
        `Total: ${Number(data.total_paid).toFixed(2)} CP · ${data.members_paid} member${data.members_paid === 1 ? '' : 's'} paid.`,
        `总计：${Number(data.total_paid).toFixed(2)} CP · ${data.members_paid} 位成员获得奖金。`
      ));
      await fetchList();
    } catch (err) {
      setError(err.message || tt('Failed to calculate', '计算失败'));
    } finally {
      setCalculating(false);
    }
  };

  const handleRelease = async (ids) => {
    if (!ids || ids.length === 0) return;
    setReleasing(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus-v2/release`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ record_ids: ids }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to release', '发放失败'));
      }
      const failures = (data.results || []).filter((r) => !r.success);
      const successes = (data.results || []).filter((r) => r.success);
      if (failures.length > 0) {
        setStatusMessage(isZh
          ? `已发放 ${successes.length} 条，${failures.length} 条失败：${failures.map((f) => f.message).join('; ')}`
          : `Released ${successes.length}, ${failures.length} failed: ${failures.map((f) => f.message).join('; ')}`);
      } else {
        setStatusMessage(tt(`Released ${successes.length} record${successes.length === 1 ? '' : 's'}.`, `已发放 ${successes.length} 条记录。`));
      }
      await fetchList();
    } catch (err) {
      setError(err.message || tt('Failed to release', '发放失败'));
    } finally {
      setReleasing(false);
    }
  };

  const handleSaveSplitRate = async (rateKey, value) => {
    setSavingRateKey(rateKey);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/update-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rate_key: rateKey, rate_percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to update rate', '更新比例失败'));
      }
      await fetchRates();
    } catch (err) {
      setError(err.message || tt('Failed to update rate', '更新比例失败'));
    } finally {
      setSavingRateKey(null);
    }
  };

  const handleSaveStarRate = async (star, value) => {
    setSavingRateKey(`star-${star}`);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus-v2/update-star-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ star, percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to update star rate', '更新星级比例失败'));
      }
      await fetchRates();
    } catch (err) {
      setError(err.message || tt('Failed to update star rate', '更新星级比例失败'));
    } finally {
      setSavingRateKey(null);
    }
  };

  const openTeamModal = async (record) => {
    setModalRecord(record);
    setModalMembers([]);
    setModalError('');
    setModalLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/team-bonus-v2/breakdown?recipient_user_id=${record.recipient?.id || ''}&year=${period.year}&month=${period.month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || tt('Failed to load team breakdown', '加载团队明细失败'));
      }
      setModalMembers(data.members || []);
    } catch (err) {
      setModalError(err.message || tt('Failed to load team breakdown', '加载团队明细失败'));
    } finally {
      setModalLoading(false);
    }
  };

  const closeTeamModal = () => {
    setModalRecord(null);
    setModalMembers([]);
    setModalError('');
  };

  const pendingRecords = useMemo(() => records.filter((r) => r.status === 'pending'), [records]);
  const releasedRecords = useMemo(() => records.filter((r) => r.status === 'released'), [records]);
  const tabRecords = statusTab === 'pending' ? pendingRecords : releasedRecords;
  const allPendingSelected = pendingRecords.length > 0 && pendingRecords.every((r) => selectedIds[r.id]);

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedIds({});
    } else {
      const next = {};
      pendingRecords.forEach((r) => { next[r.id] = true; });
      setSelectedIds(next);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return null;
  }

  const splitCashRate = payoutRates.find((r) => r.rate_key === 'TEAM_BONUS_V2_SPLIT_CASH_PERCENT');
  const splitCashPercent = splitCashRate ? Number(splitCashRate.rate_percent) : 50;

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
          {tt('Team Bonus', '团队奖金')}
        </h1>
        {activeTab !== 'rates' && (
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={`${period.year}-${String(period.month).padStart(2, '0')}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-').map((v) => parseInt(v, 10));
              if (y && m) setPeriod({ year: y, month: m });
            }}
            className="px-3 py-2 rounded-lg border text-body-sm"
            style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }}
          />
          <button
            type="button"
            onClick={handleCalculate}
            disabled={calculating || !isBjoechia}
            title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {calculating ? tt('Calculating...', '计算中...') : tt('Calculate', '计算')}
          </button>
        </div>
        )}
        {activeTab !== 'rates' && !isBjoechia && (
          <p className="text-body-xs w-full" style={{ color: '#999999' }}>
            {tt('Only WELLIS-BJOECHIA can run Calculate or Release.', '只有 WELLIS-BJOECHIA 可以执行"计算"或"发放"操作。')}
          </p>
        )}
      </div>

      <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        <TabButton active={activeTab === 'bonus'} onClick={() => setActiveTab('bonus')}>{tt('Team Bonus', '团队奖金')}</TabButton>
        <TabButton active={activeTab === 'rates'} onClick={() => setActiveTab('rates')}>{tt('Rates', '比例设置')}</TabButton>
      </div>

      {error && (
        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {error}
        </div>
      )}
      {statusMessage && (
        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
          {statusMessage}
        </div>
      )}

      {activeTab === 'bonus' ? (
        <>
          <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            {run ? (
              <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                {tt('Total paid this period', '本期发放总额')}: <span style={{ color: '#c9a961' }}>{Number(run.total_paid).toFixed(2)} CP</span>
                {' · '}{tt(`${run.members_paid} member${run.members_paid === 1 ? '' : 's'} paid`, `${run.members_paid} 位成员获得奖金`)}
              </p>
            ) : (
              <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Not yet calculated for this month.', '本月尚未计算。')}</p>
            )}
          </div>

          <div className="flex gap-2 mb-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
            <TabButton active={statusTab === 'pending'} onClick={() => setStatusTab('pending')}>{tt('Pending', '待发放')} ({pendingRecords.length})</TabButton>
            <TabButton active={statusTab === 'released'} onClick={() => setStatusTab('released')}>{tt('Released', '已发放')} ({releasedRecords.length})</TabButton>
          </div>

          {statusTab === 'pending' && pendingRecords.length > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <label className="flex items-center gap-2 text-body-sm" style={{ color: '#b8a68f' }}>
                <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAll} />
                {tt('Select all pending', '全选待发放')}
              </label>
              <button
                type="button"
                onClick={() => handleRelease(Object.keys(selectedIds).filter((id) => selectedIds[id]))}
                disabled={releasing || selectedCount === 0 || !isBjoechia}
                title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
              >
                {releasing ? tt('Releasing...', '发放中...') : tt(`Release Selected (${selectedCount})`, `发放所选 (${selectedCount})`)}
              </button>
            </div>
          )}

          {loading ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : records.length === 0 ? (
            <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt("No records yet. Click Calculate to compute this month's Team Bonus.", '暂无记录。点击"计算"以计算本月的团队奖金。')}</p>
            </div>
          ) : tabRecords.length === 0 ? (
            <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt(`No ${statusTab} records.`, `暂无${statusTab === 'pending' ? '待发放' : '已发放'}记录。`)}</p>
            </div>
          ) : (
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2"></th>
                    <th className="p-2">{tt('Recipient', '接收人')}</th>
                    <th className="p-2">{tt('Team Passive Total', '团队被动总额')}</th>
                    <th className="p-2">{tt('Star %', '星级 %')}</th>
                    <th className="p-2">{tt('Amount', '金额')}</th>
                    <th className="p-2">{tt('Cash Points', '现金积分')}</th>
                    <th className="p-2">{tt('Product Points', '产品积分')}</th>
                    <th className="p-2">{tt('Status', '状态')}</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {tabRecords.map((r) => (
                    <tr key={r.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                      <td className="p-2">
                        {r.status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={!!selectedIds[r.id]}
                            onChange={() => toggleSelect(r.id)}
                          />
                        )}
                      </td>
                      <td className="p-2" style={{ color: '#c9a961' }}>
                        {'★'.repeat(r.recipient_ranking_snapshot || 0)} {memberLabel(r.recipient)}
                      </td>
                      <td className="p-2">{Number(r.team_passive_total_snapshot || 0).toFixed(2)} CP</td>
                      <td className="p-2">{r.star_percent_snapshot}%</td>
                      <td className="p-2" style={{ color: '#c9a961' }}>{Number(r.amount).toFixed(2)} CP</td>
                      <td className="p-2">
                        {r.status === 'released'
                          ? Number(r.cash_amount).toFixed(2)
                          : (Number(r.amount) * (splitCashPercent / 100)).toFixed(2)}
                      </td>
                      <td className="p-2">
                        {r.status === 'released'
                          ? Number(r.credit_amount).toFixed(2)
                          : (Number(r.amount) * (1 - splitCashPercent / 100)).toFixed(2)}
                      </td>
                      <td className="p-2"><StatusBadge status={r.status} isZh={isZh} /></td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          {r.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => handleRelease([r.id])}
                              disabled={releasing || !isBjoechia}
                              title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                              className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                              style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                            >
                              {tt('Release', '发放')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openTeamModal(r)}
                            className="px-3 py-1 rounded font-medium text-body-xs border"
                            style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
                          >
                            {tt('View Team', '查看团队')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Team Bonus Distribution', '团队奖金分配')}</h3>
          <div className="space-y-3 mb-6">
            <NumberEditor
              label={tt('Cash Points Split', '现金积分分配比例')}
              value={splitCashRate ? Number(splitCashRate.rate_percent) : null}
              onSave={(v) => handleSaveSplitRate('TEAM_BONUS_V2_SPLIT_CASH_PERCENT', v)}
              saving={savingRateKey === 'TEAM_BONUS_V2_SPLIT_CASH_PERCENT'}
              isZh={isZh}
            />
            <p className="text-body-xs pl-3" style={{ color: '#999999' }}>
              {tt(`Product Points Split: ${(100 - splitCashPercent).toFixed(2)}% (derived)`, `产品积分分配比例：${(100 - splitCashPercent).toFixed(2)}%（自动推导）`)}
            </p>
          </div>

          <h4 className="text-body-sm font-bold mb-3" style={{ color: '#c9a961' }}>{tt('Team Bonus Star Rates', '团队奖金星级比例')}</h4>
          <div className="space-y-3">
            {starRates.map((sr) => (
              <NumberEditor
                key={sr.star}
                label={tt(`${'★'.repeat(sr.star)}${'☆'.repeat(7 - sr.star)} (${sr.star} star)`, `${'★'.repeat(sr.star)}${'☆'.repeat(7 - sr.star)}（${sr.star} 星）`)}
                value={Number(sr.percent)}
                onSave={(v) => handleSaveStarRate(sr.star, v)}
                saving={savingRateKey === `star-${sr.star}`}
                isZh={isZh}
              />
            ))}
          </div>
        </div>
      )}
    </div>

    {modalRecord && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeTeamModal}>
        <div
          className="border rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>
              {tt('Team Contribution', '团队贡献')} — {memberLabel(modalRecord.recipient)}
            </h3>
            <button
              type="button"
              onClick={closeTeamModal}
              className="px-3 py-1 rounded font-medium text-body-xs"
              style={{ backgroundColor: '#3d5a4a', color: '#b8a68f' }}
            >
              {tt('Close', '关闭')}
            </button>
          </div>

          {modalError && (
            <div className="border rounded-lg p-3 mb-4 text-body-sm" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
              {modalError}
            </div>
          )}

          {modalLoading ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : modalMembers.length === 0 ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('No contributing team members found for this period.', '本期未找到有贡献的团队成员。')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2">{tt('Member', '成员')}</th>
                    <th className="p-2">{tt('Package', '配套')}</th>
                    <th className="p-2">{tt('Passive Amount Contributed', '贡献的被动金额')}</th>
                  </tr>
                </thead>
                <tbody>
                  {modalMembers.map((m, idx) => (
                    <tr key={`${m.member?.affiliate_code || idx}`} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                      <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(m.member)}</td>
                      <td className="p-2">{m.package_code} ({Number(m.package_price || 0).toFixed(2)} CP)</td>
                      <td className="p-2">{Number(m.passive_amount).toFixed(2)} CP</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    )}
    </div>
  );
}
